/*
 * MatchScoring — broadcast-style live-scoring page for a single match.
 *
 * Route: /app/tournament-matches/:matchId/score
 *
 * Layout (B1 "Classic Lower Third" from the design handoff):
 *   1. AppShell top bar (back to event) + an in-page top bar with the
 *      course name on the left and a Team / My Match segmented toggle on
 *      the right. The toggle lives on the score page only.
 *   2. Broadcast lower-third banner — two angled bars meeting at a chevron
 *      seam, tinted by each team's primaryColor. The winning team's "2 UP /
 *      3&2 / WIN" label sits at large size on its bar.
 *   3. Stat strip: HOLE / PAR / HCP / THRU n · m LEFT.
 *   4. Hole-by-hole tile strip showing who won each hole, with the active
 *      hole highlighted in green. Tap a tile to jump.
 *   5. Player rows tinted by team color, with the par-prefilled score
 *     stepper (− N + buttons) and gold stroke chip.
 *   6. Footer: Prev | Save Hole N (primary) | Next.
 *
 * The scoring payload chooses the entry path. Supported formats post complete
 * player-score batches. Shared-ball formats post a quick hole result. Manual
 * formats submit one final match result and never enter hole scores.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Flag as FlagIcon,
  Info,
} from 'lucide-react';
import type {
  FinalResultType,
  MatchScoringParticipant,
  MatchScoringPayload,
  MatchScoringTeam,
} from '@golf-app/shared';
import {
  buildMatchHandicapExplanation,
  type StrokeAllocationSummary,
} from '@golf-app/shared';

import { api } from '@/lib/api';
import { AppShell } from '@/components/AppShell';
import { PlayerAvatar } from '@/components/PlayerAvatar';
import { matchScoringEntryMode, nextOpenMatchHole } from '@/lib/matchScoringCapabilities';
import { paletteFor, type TeamPalette } from '@/lib/teamColors';
import { deriveMatchState } from '@/lib/matchState';

const TOTAL_HOLES = 18;

type Participant = MatchScoringParticipant;
type Team = MatchScoringTeam;
type QuickHoleResult = 'team_a' | 'team_b' | 'halved';

export default function MatchScoring() {
  const { matchId } = useParams<{ matchId: string }>();
  const navigate = useNavigate();

  const [data, setData] = useState<MatchScoringPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [currentHole, setCurrentHole] = useState(1);
  // Per-player gross score for the current hole — controlled inputs, string
  // so the user can clear/backspace. Parsed at submit.
  const [scoresInput, setScoresInput] = useState<Record<string, string>>({});

  // Hole-strip scroller. The strip is a single nowrap row; the active
  // tile auto-centers (scroll clamps keep holes 1-2 left and 17-18
  // right), and the chevron overlays page the strip / dim at the edges.
  const stripRef = useRef<HTMLDivElement | null>(null);
  const activeTileRef = useRef<HTMLButtonElement | null>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const updateArrowState = useCallback(() => {
    const el = stripRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 4);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 4);
  }, []);

  const pageStrip = useCallback((dir: -1 | 1) => {
    const el = stripRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * el.clientWidth * 0.8, behavior: 'smooth' });
  }, []);

  // Center the active hole whenever it changes. Manual scrollLeft math
  // instead of scrollIntoView so the page never scrolls vertically.
  useEffect(() => {
    const strip = stripRef.current;
    const tile = activeTileRef.current;
    if (!strip || !tile) return;
    const target = tile.offsetLeft - (strip.clientWidth - tile.clientWidth) / 2;
    strip.scrollTo({ left: Math.max(0, target), behavior: 'smooth' });
    // Arrow state after the smooth scroll settles.
    const t = setTimeout(updateArrowState, 350);
    return () => clearTimeout(t);
  }, [currentHole, data, updateArrowState]);
  const [saving, setSaving] = useState(false);

  // Finalize confirmation modal — fires automatically after the save
  // that makes the match isFinal (last hole or mathematical closeout).
  // The user can confirm (POST submit-result) or go back to editing.
  const [showFinalizeModal, setShowFinalizeModal] = useState(false);
  // Owner/captain admin override — skips hole-by-hole entry and
  // finalizes the match with a chosen result. Backend re-checks role.
  const [overrideOpen, setOverrideOpen] = useState(false);
  const [overrideSubmitting, setOverrideSubmitting] = useState(false);
  const [overrideError, setOverrideError] = useState<string | null>(null);
  const [pendingManualResult, setPendingManualResult] = useState<{
    resultType: FinalResultType;
    display: string;
  } | null>(null);
  const [reopenOpen, setReopenOpen] = useState(false);
  const [reopening, setReopening] = useState(false);
  const [reopenError, setReopenError] = useState<string | null>(null);
  const [outcomeExpanded, setOutcomeExpanded] = useState(false);
  const [popsInfoOpen, setPopsInfoOpen] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const [finalizeError, setFinalizeError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!matchId) return;
    setError(null);
    try {
      const payload = await api.getMatchScoring(matchId);
      setData(payload);
      // Default to the next unscored hole (first hole missing a match_holes row).
      const scoredNumbers = new Set(payload.matchHoles.map((h) => h.holeNumber));
      let next = 1;
      while (next <= TOTAL_HOLES && scoredNumbers.has(next)) next++;
      if (next > TOTAL_HOLES) next = TOTAL_HOLES;
      setCurrentHole((prev) => (prev === 1 && payload.matchHoles.length > 0 ? next : prev));
    } catch (e: any) {
      setError(e?.message ?? "Couldn't load match");
    } finally {
      setLoading(false);
    }
  }, [matchId]);

  useEffect(() => { load(); }, [load]);

  // When the current hole changes, prefill the inputs.
  // Priority:
  //   1) A previously-recorded gross score for this player on this hole.
  //   2) The hole's par (so the user only bumps up or down off par instead
  //      of typing every score from scratch).
  //   3) Empty string if we don't even know the par (course data missing).
  useEffect(() => {
    if (!data) return;
    const matchHole = data.matchHoles.find((h) => h.holeNumber === currentHole);
    const recorded = new Map<string, number>();
    if (matchHole) {
      for (const s of data.holeScores) {
        if (s.matchHoleId === matchHole.id) recorded.set(s.eventPlayerId, s.grossScore);
      }
    }
    const par = data.holes.find((h) => h.holeNumber === currentHole)?.par ?? null;
    const defaultStr = par != null ? String(par) : '';
    const next: Record<string, string> = {};
    for (const p of data.participants) {
      const r = recorded.get(p.eventPlayerId);
      next[p.eventPlayerId] = r != null ? String(r) : defaultStr;
    }
    setScoresInput(next);
  }, [data, currentHole]);

  const holeInfo = useMemo(() => data?.holes.find((h) => h.holeNumber === currentHole) ?? null, [data, currentHole]);

  // Resolve each side's team and its broadcast palette.
  const teamA = useMemo(() => data?.teams.find((t) => t.id === data.match.teamAId) ?? null, [data]);
  const teamB = useMemo(() => data?.teams.find((t) => t.id === data.match.teamBId) ?? null, [data]);
  const palettes = useMemo(() => ({
    a: paletteFor(teamA?.primaryColor ?? null, 1),
    b: paletteFor(teamB?.primaryColor ?? null, 2),
  }), [teamA, teamB]);

  // Live status: which side is up / down / all square, by how much, thru
  // what hole, with how many holes left to play. Mirrors the API's
  // computeMatchState; the API already includes a `liveState.state.display`
  // so for the banner numeral we just parse that, with a defensive fallback.
  const status = useMemo(() => deriveStatus(data, palettes), [data, palettes]);

  async function save() {
    if (!data || !matchId) return;
    setError(null);
    if (!data.capabilities.supportsDetailedScoring || data.match.formatType === 'manual_result_only') {
      setError('This format uses result controls instead of individual player scores.');
      return;
    }
    if (!data.permissions.canScore) {
      setError(!['locked', 'in_progress'].includes(data.round?.status ?? '')
        ? 'Score entry opens after the round is locked for scoring.'
        : 'You can view this match, but you do not have scoring permission.');
      return;
    }
    const scores: { eventPlayerId: string; grossScore: number }[] = [];
    for (const p of data.participants) {
      const raw = (scoresInput[p.eventPlayerId] ?? '').trim();
      if (raw === '') continue;
      const n = parseInt(raw, 10);
      if (!Number.isInteger(n) || n < 1 || n > 20) {
        setError(`Score for ${p.displayName} must be a whole number between 1 and 20.`);
        return;
      }
      scores.push({ eventPlayerId: p.eventPlayerId, grossScore: n });
    }
    if (scores.length === 0) {
      setError('Enter at least one score before saving.');
      return;
    }
    setSaving(true);
    try {
      await api.postHoleScores(matchId, currentHole, scores);
      // Re-fetch /scoring so liveState reflects the just-saved hole.
      const payload = await api.getMatchScoring(matchId);
      setData(payload);

      // Did this save just close the match out? deriveMatchState gives
      // us isFinal + status check so we can prompt the user to submit
      // the result instead of silently advancing past the last hole.
      const justFinished = (() => {
        const m = payload.match as any;
        if (!m) return false;
        if (m.status === 'final' || m.status === 'pending_confirmation') return false;
        return Boolean(payload.liveState?.state.isFinal);
      })();

      if (justFinished) {
        setShowFinalizeModal(true);
      } else {
        setCurrentHole((h) => Math.min(TOTAL_HOLES, h + 1));
      }
    } catch (e: any) {
      setError(e?.message ?? 'Failed to save scores.');
    } finally {
      setSaving(false);
    }
  }

  async function saveQuickResult(result: QuickHoleResult) {
    if (!data || !matchId) return;
    setError(null);
    if (data.capabilities.supportsDetailedScoring || data.match.formatType === 'manual_result_only') {
      setError('Quick hole results are not available for this scoring format.');
      return;
    }
    if (!data.permissions.canScore) {
      setError('You can view this match, but you do not have scoring permission.');
      return;
    }

    setSaving(true);
    try {
      await api.quickHoleResult(matchId, currentHole, result);
      const payload = await api.getMatchScoring(matchId);
      setData(payload);
      const justFinished = !['final', 'pending_confirmation'].includes(payload.match.status)
        && payload.liveState.state.isFinal;
      if (justFinished) {
        setShowFinalizeModal(true);
      } else {
        setCurrentHole(nextOpenMatchHole(payload.matchHoles, currentHole, TOTAL_HOLES));
      }
    } catch (e: any) {
      setError(e?.message ?? 'Failed to save the hole result.');
    } finally {
      setSaving(false);
    }
  }

  async function submitManualResult(resultType: FinalResultType, display: string): Promise<boolean> {
    if (!data || !matchId || data.match.formatType !== 'manual_result_only') return false;
    setError(null);
    const canDeclareResult = data.permissions.canDeclareResult
      ?? data.permissions.canSetFallbackResult;
    const canSelectManualResult = data.permissions.canSelectManualResult
      ?? (canDeclareResult || data.permissions.canSubmit);
    if (!canSelectManualResult) {
      setError('Only an authorized match scorer, captain, or event owner can choose this result.');
      return false;
    }

    setSaving(true);
    try {
      if (canDeclareResult) {
        await api.adminOverrideMatch(matchId, { resultType, display });
      } else {
        await api.submitMatchResult(matchId, { resultType, display });
      }
      await load();
      return true;
    } catch (e: any) {
      setError(e?.message ?? 'Failed to submit the match result.');
      return false;
    } finally {
      setSaving(false);
    }
  }

  async function reopenMatch() {
    if (!matchId) return;
    setReopenError(null);
    setReopening(true);
    try {
      await api.reopenMatchResult(matchId);
      setReopenOpen(false);
      await load();
    } catch (e: any) {
      setReopenError(e?.message ?? 'Could not resume scoring.');
    } finally {
      setReopening(false);
    }
  }

  async function confirmFinalize() {
    if (!matchId) return;
    setFinalizeError(null);
    setFinalizing(true);
    try {
      await api.submitMatchResult(matchId);
      // Refresh so the match status flips to final (the API finalizes
      // immediately — no captain confirmation step).
      await load();
      setShowFinalizeModal(false);
      // Bounce back to the round-matches overview so the user sees
      // their finalized match alongside the rest of the round.
      const roundId = (data?.match as any)?.roundId;
      if (roundId) navigate(`/app/tournament-rounds/${roundId}/matches`);
    } catch (e: any) {
      setFinalizeError(e?.message ?? 'Could not submit result.');
    } finally {
      setFinalizing(false);
    }
  }

  if (loading) {
    return (
      <AppShell back={{ to: '/app/home', label: 'Home' }}>
        <div className="t-loading"><div className="spinner" /></div>
      </AppShell>
    );
  }
  if (error && !data) {
    return (
      <AppShell back={{ to: '/app/home', label: 'Home' }}>
        <div className="t-empty">
          <div className="t-empty-title">Match not available</div>
          <div className="t-empty-hint">{error}</div>
        </div>
      </AppShell>
    );
  }
  if (!data) return null;

  const eventBack = { to: `/app/tournaments/${data.match.eventId}`, label: 'Event' };
  // Final matches become read-only navigation: Prev/Next stop saving.
  const isMatchFinal = data.match.status === 'final';
  const entryMode = matchScoringEntryMode(
    data.match.formatType,
    data.capabilities.supportsDetailedScoring,
  );
  const currentMatchHole = data.matchHoles.find((hole) => hole.holeNumber === currentHole);
  const lifecycleLocked = ['pending_confirmation', 'disputed', 'final', 'cancelled']
    .includes(data.match.status);
  const canEditCurrentHole = data.permissions.canScore
    && !lifecycleLocked
    && (!data.liveState.state.isFinal || currentMatchHole?.status === 'completed');
  const currentOutcome = currentMatchHole?.status !== 'completed'
    ? null
    : currentMatchHole.isHalved
      ? 'halved'
      : currentMatchHole.winnerTeamId === data.match.teamAId
        ? 'team_a'
        : currentMatchHole.winnerTeamId === data.match.teamBId
          ? 'team_b'
          : null;
  const manualOutcome = data.match.finalResultType === 'team_a_win'
    ? 'team_a'
    : data.match.finalResultType === 'team_b_win'
      ? 'team_b'
      : data.match.finalResultType === 'tie'
        ? 'halved'
        : null;
  // Owners and captains can declare or correct the result at every stage of
  // an active event. The server remains authoritative for the role check.
  const canOverride = data.permissions.canDeclareResult;
  const canSelectManualResult = data.permissions.canSelectManualResult
    ?? (canOverride
      || (entryMode === 'manual-result' && data.permissions.canSubmit));
  const canReopen = isMatchFinal
    && data.round?.status === 'in_progress'
    && (data.permissions.canReopen ?? canOverride);
  const outcomeSummary = isMatchFinal
    ? (data.match.finalResultDisplay ?? 'Final')
    : data.match.status === 'pending_confirmation'
      ? 'Awaiting confirmation'
      : data.match.status === 'disputed'
        ? 'Result in review'
        : status.played > 0
          ? status.side === 'tie' ? 'All square' : status.label
          : 'Scoring in progress';

  async function submitOverride(resultType: 'team_a_win' | 'team_b_win' | 'tie', reason: string) {
    setOverrideError(null);
    setOverrideSubmitting(true);
    try {
      await api.adminOverrideMatch(data!.match.id, { resultType, reason: reason.trim() || undefined });
      setOverrideOpen(false);
      // Refetch the match so the page reflects its new final state.
      await load();
    } catch (e: any) {
      setOverrideError(e?.message ?? 'Failed to override the match result.');
    } finally {
      setOverrideSubmitting(false);
    }
  }
  const sideA = data.participants.filter((p) => p.sideNumber === 1);
  const sideB = data.participants.filter((p) => p.sideNumber === 2);
  const roundMatchesHref = `/app/tournament-rounds/${data.match.roundId}/matches`;

  return (
    <AppShell back={eventBack} fullBleed>
      <div style={{ background: 'var(--bcast-bg)', minHeight: '100%', color: 'var(--bcast-ink-1)' }}>
        {/* ── In-page top row with course title + Team / My Match toggle ── */}
        <div
          className="bcast-topbar-row"
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            gap: 14,
            borderBottom: '1px solid var(--bcast-line)',
            flexWrap: 'wrap',
          }}
        >
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--bcast-ink-1)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {data.round?.courseName ?? data.round?.name ?? 'Match scoring'}
            </div>
            <div className="bcast-course-sub" style={{ fontSize: 12, color: 'var(--bcast-ink-3)', fontWeight: 600, marginTop: 2 }}>
              {data.round?.teeName ? `${data.round.teeName} tees` : ''}
              {data.round?.parTotal ? `${data.round.teeName ? ' · ' : ''}Par ${data.round.parTotal}` : ''}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--s-lg)' }}>
            <button
              type="button"
              className="t-btn t-btn-ghost"
              onClick={() => setPopsInfoOpen(true)}
              aria-label="How handicap pops work for this match"
              style={{ minWidth: 'var(--touch)', minHeight: 'var(--touch)', padding: 'var(--s-lg)' }}
            >
              <Info width={18} height={18} />
              <span>Pops</span>
            </button>
            <div className="bcast-toggle" role="tablist" aria-label="Match view">
              <button
                type="button"
                className="bcast-toggle-btn"
                onClick={() => navigate(roundMatchesHref)}
                role="tab"
                aria-selected={false}
              >
                Team
              </button>
              <button
                type="button"
                className="bcast-toggle-btn is-active"
                role="tab"
                aria-selected={true}
              >
                My Match
              </button>
            </div>
          </div>
        </div>

        {/* ── Broadcast banner: two angled bars with status numerals ── */}
        <div style={{ position: 'relative' }} className="bcast-banner">
          {/* Left bar (Team A) */}
          <div
            className="bcast-banner-side left"
            style={{ background: `linear-gradient(95deg, ${palettes.a.solid}, ${palettes.a.deep})` }}
          >
            <div style={{ minWidth: 0, flex: 1 }}>
              <div className="bcast-banner-label">
                TEAM {teamLabel(teamA?.name, 'A')}
              </div>
              <div className="bcast-banner-names">
                {namesFor(sideA)}
              </div>
            </div>
            {status.side === 'a' && status.label && (
              <div className="bcast-banner-status bcast-tnum" aria-label={`${teamA?.name ?? 'Team A'} leads ${status.label}`}>
                <ArrowLeft width={18} height={18} aria-hidden="true" />
                {status.label}
              </div>
            )}
          </div>
          {/* Right bar (Team B) */}
          <div
            className="bcast-banner-side right"
            style={{
              background: `linear-gradient(265deg, ${palettes.b.solid}, ${palettes.b.deep})`,
              justifyContent: status.side === 'b' && status.label ? 'space-between' : 'flex-end',
            }}
          >
            {status.side === 'b' && status.label && (
              <div className="bcast-banner-status bcast-tnum" aria-label={`${teamB?.name ?? 'Team B'} leads ${status.label}`}>
                {status.label}
                <ArrowRight width={18} height={18} aria-hidden="true" />
              </div>
            )}
            <div style={{ minWidth: 0, flex: 1, textAlign: 'right' }}>
              <div className="bcast-banner-label" style={{ textAlign: 'right' }}>
                TEAM {teamLabel(teamB?.name, 'B')}
              </div>
              <div className="bcast-banner-names">
                {namesFor(sideB)}
              </div>
            </div>
          </div>
          {/* All Square overlay sits centered above the seam when neither
              side is leading and at least one hole has been played. */}
          {status.side === 'tie' && status.played > 0 && (
            <div className="bcast-banner-allsquare">ALL SQUARE</div>
          )}
        </div>

        {/* Manual-result matches have no hole selector or hole context row. */}
        {entryMode === 'manual-result' ? (
          <div className="bcast-stat-strip">
            <div className="bcast-stat-cell">FINAL RESULT</div>
            <div className="bcast-stat-cell live">NO HOLE ENTRY</div>
          </div>
        ) : null}

        {/* Error banner if save failed */}
        {error && data && (
          <div className="bcast-pad-x" style={{ marginTop: 12 }}><div className="t-error" style={{ margin: 0 }}>{error}</div></div>
        )}

        {/* ── Hole-by-hole tile strip ── */}
        {entryMode !== 'manual-result' && (
        <div className="bcast-pad-x" style={{ paddingTop: 14 }}>
          {/* Keep the section label above the selector. Hole context and the
              result legend sit below it in the same order on every screen. */}
          <div
            className="bcast-strip-header"
            style={{
              display: 'flex', alignItems: 'center',
              marginBottom: 8,
            }}
          >
            <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.1em', color: 'var(--bcast-ink-3)' }}>
              HOLE BY HOLE
            </span>
          </div>
          {/* Single scrolling row (no wrap). The active hole auto-centers
              when there's room — holes 1-2 naturally pin left and 17-18
              pin right because scroll clamps at the edges. Chevron
              overlays signal scrollability and page the strip on tap. */}
          <div className="bcast-hole-scroller">
            <button
              type="button"
              className={`bcast-hole-arrow left${canScrollLeft ? '' : ' is-edge'}`}
              onClick={() => pageStrip(-1)}
              aria-label="Scroll holes left"
              tabIndex={-1}
            >
              <ChevronLeft width={16} height={16} />
            </button>
            <div className="bcast-hole-strip" ref={stripRef} onScroll={updateArrowState}>
              {Array.from({ length: TOTAL_HOLES }, (_, i) => i + 1).map((n) => {
                const mh = data.matchHoles.find((h) => h.holeNumber === n) ?? null;
                const isActive = n === currentHole;
                const winner = mh?.winnerTeamId ?? null;
                const isHalved = mh?.isHalved ?? false;
                const wonByA = winner && winner === data.match.teamAId;
                const wonByB = winner && winner === data.match.teamBId;
                const tileStyle: React.CSSProperties = {};
                let className = 'bcast-hole-tile';
                if (isActive) {
                  className += ' is-active';
                } else if (wonByA) {
                  tileStyle.background = palettes.a.soft;
                  tileStyle.borderColor = palettes.a.border;
                  tileStyle.color = palettes.a.solid;
                } else if (wonByB) {
                  tileStyle.background = palettes.b.soft;
                  tileStyle.borderColor = palettes.b.border;
                  tileStyle.color = palettes.b.solid;
                } else if (isHalved) {
                  className += ' is-halved';
                }
                return (
                  <button
                    key={n}
                    type="button"
                    ref={isActive ? activeTileRef : undefined}
                    onClick={() => setCurrentHole(n)}
                    className={className}
                    style={tileStyle}
                    aria-label={`Hole ${n}`}
                  >
                    {n}
                  </button>
                );
              })}
            </div>
            <button
              type="button"
              className={`bcast-hole-arrow right${canScrollRight ? '' : ' is-edge'}`}
              onClick={() => pageStrip(1)}
              aria-label="Scroll holes right"
              tabIndex={-1}
            >
              <ChevronRight width={16} height={16} />
            </button>
          </div>
          <div className="bcast-hole-context">
            <span>HOLE {currentHole}</span>
            <span>HCP {holeInfo?.strokeIndex ?? '—'}</span>
            <span>PAR {holeInfo?.par ?? '—'}</span>
            <span className="is-live">THRU {status.played} · {status.remaining} LEFT</span>
          </div>
          <HoleLegend
            palettes={palettes}
            teamAName={teamA?.name ?? 'Team A'}
            teamBName={teamB?.name ?? 'Team B'}
          />
        </div>
        )}

        {entryMode === 'detailed' ? (
          /* ── Score entry: 2-column grid on web, stacked w/ VS on mobile ── */
          <div className="bcast-score-grid">
            <div className="bcast-score-side">
              {sideA.map((p) => (
                <BroadcastPlayerRow
                  key={p.eventPlayerId}
                  player={p}
                  palette={palettes.a}
                  teamName={teamA?.name ?? 'Team A'}
                  currentHole={currentHole}
                  par={holeInfo?.par ?? null}
                  value={scoresInput[p.eventPlayerId] ?? ''}
                  disabled={!canEditCurrentHole || saving}
                  onChange={(v) => setScoresInput((cur) => ({ ...cur, [p.eventPlayerId]: v }))}
                />
              ))}
            </div>
            <div className="bcast-vs">
              <span className="bar" />VS<span className="bar" />
            </div>
            <div className="bcast-score-side">
              {sideB.map((p) => (
                <BroadcastPlayerRow
                  key={p.eventPlayerId}
                  player={p}
                  palette={palettes.b}
                  teamName={teamB?.name ?? 'Team B'}
                  currentHole={currentHole}
                  par={holeInfo?.par ?? null}
                  value={scoresInput[p.eventPlayerId] ?? ''}
                  disabled={!canEditCurrentHole || saving}
                  onChange={(v) => setScoresInput((cur) => ({ ...cur, [p.eventPlayerId]: v }))}
                />
              ))}
            </div>
          </div>
        ) : entryMode === 'quick-hole' ? (
          <BroadcastResultControls
            eyebrow={`HOLE ${currentHole} RESULT`}
            title="Who won the hole?"
            helper="Record the team result for this hole. Individual scores are not used for this format."
            teamAName={teamA?.name ?? 'Team A'}
            teamBName={teamB?.name ?? 'Team B'}
            accentA={palettes.a.solid}
            softA={palettes.a.soft}
            accentB={palettes.b.solid}
            softB={palettes.b.soft}
            selected={currentOutcome}
            busy={saving}
            disabled={!canEditCurrentHole}
            readOnlyMessage={data.permissions.canScore
              ? 'This match is locked. You can still review recorded hole results.'
              : !['locked', 'in_progress'].includes(data.round?.status ?? '')
                ? 'Score entry opens after the round is locked for scoring.'
                : 'You can review this match. Any player in the match can enter its shared score.'}
            onSelect={(result) => void saveQuickResult(result)}
          />
        ) : null}

        {entryMode === 'detailed' && !data.permissions.canScore && (
          <div className="t-error" style={{ borderColor: 'var(--gold)', color: 'var(--gold)', background: 'rgba(212,175,55,0.08)' }}>
            {!['locked', 'in_progress'].includes(data.round?.status ?? '')
              ? 'Score entry opens after the round is locked for scoring.'
              : 'This match is read-only for your account. Pull to refresh or ask the captain to verify the pairing.'}
          </div>
        )}

        {/* Detailed mode saves through Next. Quick-result mode saves through
            the result choices, so its footer only navigates between holes. */}
        {entryMode !== 'manual-result' && (
        <div className="bcast-footer">
          <button
            type="button"
            className="bcast-footer-side"
            disabled={currentHole <= 1 || saving}
            onClick={() => setCurrentHole((h) => Math.max(1, h - 1))}
            aria-label="Previous hole"
          >
            <ChevronLeft width={16} height={16} /> Prev
          </button>
          {isMatchFinal ? (
            <button
              type="button"
              className="bcast-save"
              disabled={currentHole >= TOTAL_HOLES}
              onClick={() => setCurrentHole((h) => Math.min(TOTAL_HOLES, h + 1))}
            >
              {currentHole >= TOTAL_HOLES ? 'Match Final' : <>Next Hole <ChevronRight width={16} height={16} /></>}
            </button>
          ) : entryMode === 'detailed' ? (
            <button
              type="button"
              className="bcast-save"
              disabled={saving || !canEditCurrentHole}
              onClick={save}
            >
              {saving ? 'Saving…' : currentHole >= TOTAL_HOLES ? (
                <><FlagIcon width={16} height={16} /> Finish Round</>
              ) : (
                <>Next Hole <ChevronRight width={16} height={16} /></>
              )}
            </button>
          ) : (
            <button
              type="button"
              className="bcast-save"
              disabled={saving}
              onClick={() => setCurrentHole(nextOpenMatchHole(data.matchHoles, currentHole, TOTAL_HOLES))}
            >
              Next open hole <ChevronRight width={16} height={16} />
            </button>
          )}
        </div>
        )}

        <section className="bcast-outcome-disclosure">
          <button
            type="button"
            className="bcast-outcome-trigger"
            onClick={() => setOutcomeExpanded((current) => !current)}
            aria-expanded={outcomeExpanded}
            aria-controls="match-outcome-controls"
          >
            <span className="bcast-outcome-copy">
              <span className="bcast-outcome-title">MATCH OUTCOME</span>
              <span className="bcast-outcome-summary">{outcomeSummary}</span>
            </span>
            {outcomeExpanded
              ? <ChevronUp width={20} height={20} aria-hidden="true" />
              : <ChevronDown width={20} height={20} aria-hidden="true" />}
          </button>
          {outcomeExpanded && (
            <div id="match-outcome-controls" className="bcast-outcome-body">
              {entryMode === 'manual-result' && canSelectManualResult ? (
                <BroadcastResultControls
                  eyebrow="FINAL MATCH RESULT"
                  title={isMatchFinal ? 'Result recorded' : 'Choose the final result'}
                  helper="This format skips hole-by-hole scoring and records one final match result."
                  teamAName={teamA?.name ?? 'Team A'}
                  teamBName={teamB?.name ?? 'Team B'}
                  accentA={palettes.a.solid}
                  softA={palettes.a.soft}
                  accentB={palettes.b.solid}
                  softB={palettes.b.soft}
                  selected={manualOutcome}
                  busy={saving}
                  disabled={!canOverride && lifecycleLocked}
                  readOnlyMessage={!canOverride && lifecycleLocked
                    ? `Result status: ${data.match.finalResultDisplay ?? data.match.status.replaceAll('_', ' ')}`
                    : undefined}
                  onSelect={(result) => {
                    setError(null);
                    if (result === 'team_a') {
                      setPendingManualResult({
                        resultType: 'team_a_win',
                        display: `${teamA?.name ?? 'Team A'} win`,
                      });
                    } else if (result === 'team_b') {
                      setPendingManualResult({
                        resultType: 'team_b_win',
                        display: `${teamB?.name ?? 'Team B'} win`,
                      });
                    } else {
                      setPendingManualResult({ resultType: 'tie', display: 'Halved' });
                    }
                  }}
                />
              ) : null}

              {entryMode === 'manual-result' && !canSelectManualResult ? (
                <p className="bcast-outcome-info">
                  {lifecycleLocked
                    ? `Result status: ${data.match.finalResultDisplay ?? data.match.status.replaceAll('_', ' ')}`
                    : 'Only an authorized match scorer, captain, or event owner can choose this result.'}
                </p>
              ) : null}

              {canReopen ? (
                <div className="bcast-outcome-action-row">
                  <div>
                    <div className="bcast-outcome-action-title">Wrong result?</div>
                    <div className="bcast-outcome-action-help">Resume scoring without losing saved hole scores.</div>
                  </div>
                  <button
                    type="button"
                    className="t-btn t-btn-primary"
                    onClick={() => { setReopenError(null); setReopenOpen(true); }}
                  >
                    Resume scoring
                  </button>
                </div>
              ) : null}

              {canOverride && entryMode !== 'manual-result' ? (
                <button
                  type="button"
                  className="bcast-outcome-secondary"
                  onClick={() => { setOverrideError(null); setOverrideOpen(true); }}
                >
                  Declare or correct result
                </button>
              ) : null}

              {entryMode !== 'manual-result' && !canReopen && !canOverride ? (
                <p className="bcast-outcome-info">
                  Keep scoring hole by hole. Final result controls appear when the match is complete.
                </p>
              ) : null}
            </div>
          )}
        </section>
      </div>

      {overrideOpen && (
        <OverrideResultModal
          teamA={teamA}
          teamB={teamB}
          accentA={palettes.a.solid}
          accentB={palettes.b.solid}
          busy={overrideSubmitting}
          error={overrideError}
          onCancel={() => { if (!overrideSubmitting) setOverrideOpen(false); }}
          onConfirm={submitOverride}
        />
      )}

      {pendingManualResult && (
        <MatchActionConfirmationModal
          title="Confirm match result"
          resultLabel={pendingManualResult.display}
          description="This will finalize the match and update the team standings."
          confirmLabel="Confirm result"
          busy={saving}
          error={error}
          onCancel={() => {
            if (!saving) {
              setPendingManualResult(null);
              setError(null);
            }
          }}
          onConfirm={() => {
            const pending = pendingManualResult;
            void submitManualResult(pending.resultType, pending.display).then((saved) => {
              if (saved) setPendingManualResult(null);
            });
          }}
        />
      )}

      {reopenOpen && (
        <MatchActionConfirmationModal
          title="Resume scoring?"
          resultLabel={`Remove “${data.match.finalResultDisplay ?? 'Final result'}”`}
          description="This removes the final result and its awarded points. All saved hole scores will be kept."
          confirmLabel="Resume scoring"
          busy={reopening}
          error={reopenError}
          onCancel={() => { if (!reopening) setReopenOpen(false); }}
          onConfirm={() => void reopenMatch()}
        />
      )}

      {showFinalizeModal && (
        <FinalizeModal
          winnerName={
            status.side === 'a' ? (teamA?.name ?? 'Team A')
              : status.side === 'b' ? (teamB?.name ?? 'Team B')
              : null
          }
          marginLabel={status.label}
          accentColor={
            status.side === 'a' ? palettes.a.solid
              : status.side === 'b' ? palettes.b.solid
              : 'var(--bcast-ink-2)'
          }
          busy={finalizing}
          error={finalizeError}
          onConfirm={confirmFinalize}
          onEdit={() => { setShowFinalizeModal(false); setFinalizeError(null); }}
        />
      )}

      {popsInfoOpen && (
        <PopsInfoModal
          match={data.match}
          calculation={data.handicapCalculation}
          participants={data.participants}
          teamAName={teamA?.name ?? 'Team A'}
          teamBName={teamB?.name ?? 'Team B'}
          currentHole={currentHole}
          onClose={() => setPopsInfoOpen(false)}
        />
      )}
    </AppShell>
  );
}

function formatAllocationHoles(summary: StrokeAllocationSummary): string {
  if (summary.holes.length === 0) return 'No pop holes';
  return summary.holes
    .map((hole) => hole.strokes > 1
      ? `${hole.holeNumber} ×${hole.strokes}`
      : String(hole.holeNumber))
    .join(', ');
}

function formatHandicapStep(value: number | null): string {
  return value == null ? '—' : fmtHcp(value);
}

function popTotalLabel(total: number): string {
  return total > 0 ? `${total} POP${total === 1 ? '' : 'S'}` : 'NO POPS';
}

function matchStrokeCalculation(
  playingHandicap: number | null,
  baseline: number | null,
  rawMatchStrokes: number | null,
  totalPops: number,
): string {
  if (playingHandicap == null || baseline == null || rawMatchStrokes == null) {
    return `Saved allocation · ${popTotalLabel(totalPops).toLowerCase()}`;
  }
  return `${formatHandicapStep(playingHandicap)} − ${formatHandicapStep(baseline)} baseline = ${formatHandicapStep(rawMatchStrokes)} → ${popTotalLabel(totalPops).toLowerCase()}`;
}

function PopsInfoModal({
  match,
  calculation,
  participants,
  teamAName,
  teamBName,
  currentHole,
  onClose,
}: {
  match: MatchScoringPayload['match'];
  calculation: MatchScoringPayload['handicapCalculation'];
  participants: MatchScoringParticipant[];
  teamAName: string;
  teamBName: string;
  currentHole: number;
  onClose: () => void;
}) {
  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const explanation = buildMatchHandicapExplanation({ match, participants, calculation });
  const settings = explanation.settings;
  const allocationRows = explanation.isTeamFormat ? explanation.teams : explanation.players;
  const currentPops = allocationRows
    .map((row) => ({
      label: 'label' in row
        ? row.label
        : row.sideNumber === 1 ? teamAName : teamBName,
      strokes: row.pops.holes.find((hole) => hole.holeNumber === currentHole)?.strokes ?? 0,
    }))
    .filter((row) => row.strokes > 0);
  const settingsTitle = settings
    ? `${settings.handicapMethod === 'course' ? 'COURSE HCP' : 'STRAIGHT HCP'} · ${formatHandicapStep(settings.allowancePct)}%`
    : 'SAVED MATCH ALLOCATION';
  const settingsDetail = settings
    ? settings.baselineMethod === 'full_difference'
      ? 'Full difference · Baseline 0'
      : `Low player to zero · Baseline ${formatHandicapStep(settings.baselinePlayingHandicap)}`
    : 'Calculation settings will appear after the scoring API refreshes.';

  return (
    <div className="bcast-modal-overlay" role="presentation" onClick={onClose}>
      <section
        className="bcast-pops-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="pops-info-title"
        onClick={(event) => event.stopPropagation()}
      >
        <header className="bcast-pops-header">
          <div>
            <div className="bcast-pops-eyebrow">MATCH HANDICAPS</div>
            <h2 id="pops-info-title" className="bcast-pops-title">How pops work</h2>
          </div>
          <button type="button" className="bcast-pops-close" onClick={onClose} aria-label="Close pop explanation">
            Close
          </button>
        </header>

        <div className="bcast-pops-content">
          {explanation.isGross ? (
            <p className="bcast-pops-message">
              This is a gross match. Handicap strokes are not applied, so nobody pops.
            </p>
          ) : (
            <>
              <div className="bcast-pop-settings">
                <strong className="bcast-pop-settings-title">{settingsTitle}</strong>
                <span className="bcast-pop-settings-detail">{settingsDetail}</span>
                {explanation.isTeamFormat && match.formatType === 'scramble_match_play' && settings ? (
                  <span className="bcast-pop-settings-detail">
                    Team HCP · {formatHandicapStep(settings.scrambleLowPlayerPct)}% low + {formatHandicapStep(settings.scrambleHighPlayerPct)}% high
                  </span>
                ) : explanation.isTeamFormat && match.formatType === 'foursomes_alternate_shot' ? (
                  <span className="bcast-pop-settings-detail">Team HCP · 50% of combined playing handicaps</span>
                ) : null}
              </div>
              <div className="bcast-current-pops">
                <span>HOLE {currentHole}</span>
                <strong>
                  {currentPops.length > 0
                    ? currentPops.map((row) => `${row.label} +${row.strokes}`).join(' · ')
                    : 'No pops on this hole'}
                </strong>
              </div>
              <div className="bcast-allocation-list">
                {explanation.isTeamFormat ? explanation.teams.map((team) => {
                  const teamName = team.sideNumber === 1 ? teamAName : teamBName;
                  const playingValues = team.players
                    .map((player) => player.playingHandicap)
                    .filter((value): value is number => value != null)
                    .sort((left, right) => left - right);
                  const low = playingValues[0] ?? null;
                  const high = playingValues[playingValues.length - 1] ?? low;
                  const teamFormula = team.usesTeamOverride
                    ? `Team HCP override · ${formatHandicapStep(team.playingHandicap)}`
                    : match.formatType === 'scramble_match_play'
                      ? settings
                        ? `${formatHandicapStep(settings.scrambleLowPlayerPct)}% × ${formatHandicapStep(low)} + ${formatHandicapStep(settings.scrambleHighPlayerPct)}% × ${formatHandicapStep(high)} = Team HCP ${formatHandicapStep(team.playingHandicap)}`
                        : `Saved scramble team HCP · ${formatHandicapStep(team.playingHandicap)}`
                      : `50% × combined playing HCPs = Team HCP ${formatHandicapStep(team.playingHandicap)}`;
                  return (
                    <div key={team.key} className="bcast-allocation-row">
                      <div className="bcast-allocation-top">
                        <div className="bcast-allocation-name">
                          <strong>{teamName}</strong>
                          <span>{teamFormula}</span>
                        </div>
                        <span className="bcast-allocation-total">{popTotalLabel(team.pops.totalStrokes)}</span>
                      </div>
                      <div className="bcast-allocation-calculation">
                        {matchStrokeCalculation(
                          team.playingHandicap,
                          settings?.baselinePlayingHandicap ?? null,
                          team.rawMatchStrokes,
                          team.pops.totalStrokes,
                        )}
                      </div>
                      <div className="bcast-allocation-holes">Holes · {formatAllocationHoles(team.pops)}</div>
                      <div className="bcast-player-inputs">
                        {team.players.map((player) => (
                          <div key={player.key} className="bcast-player-input-row">
                            <span className="bcast-player-input-name">{player.label}</span>
                            <span className="bcast-player-input-value">
                              Index {formatHandicapStep(player.handicapIndex)} → Playing {formatHandicapStep(player.playingHandicap)}
                              {player.usesIndexOverride || player.usesCourseOverride || player.usesPlayingOverride ? ' · override' : ''}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                }) : explanation.players.map((player) => (
                  <div key={player.key} className="bcast-allocation-row">
                    <div className="bcast-allocation-top">
                      <div className="bcast-allocation-name">
                        <strong>{player.label}</strong>
                        <span>
                          {player.usesIndexOverride ? 'Index override' : 'Index'} {formatHandicapStep(player.handicapIndex)}
                          {settings?.handicapMethod === 'course'
                            ? ` → ${player.usesCourseOverride ? 'Course override' : 'Course'} ${formatHandicapStep(player.courseHandicap)}`
                            : ''}
                          {` → ${player.usesPlayingOverride ? 'Playing override' : 'Playing'} ${formatHandicapStep(player.playingHandicap)}`}
                        </span>
                      </div>
                      <span className="bcast-allocation-total">{popTotalLabel(player.pops.totalStrokes)}</span>
                    </div>
                    <div className="bcast-allocation-calculation">
                      {matchStrokeCalculation(
                        player.playingHandicap,
                        settings?.baselinePlayingHandicap ?? null,
                        player.rawMatchStrokes,
                        player.pops.totalStrokes,
                      )}
                    </div>
                    <div className="bcast-allocation-holes">Holes · {formatAllocationHoles(player.pops)}</div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        <button type="button" className="bcast-save bcast-pops-done" onClick={onClose}>
          Got it
        </button>
      </section>
    </div>
  );
}

// ── BroadcastResultControls ──────────────────────────────────────────────
function BroadcastResultControls({
  eyebrow,
  title,
  helper,
  teamAName,
  teamBName,
  accentA,
  softA,
  accentB,
  softB,
  selected,
  busy,
  disabled,
  readOnlyMessage,
  onSelect,
}: {
  eyebrow: string;
  title: string;
  helper: string;
  teamAName: string;
  teamBName: string;
  accentA: string;
  softA: string;
  accentB: string;
  softB: string;
  selected: QuickHoleResult | null;
  busy: boolean;
  disabled: boolean;
  readOnlyMessage?: string;
  onSelect: (result: QuickHoleResult) => void;
}) {
  const choices: Array<{
    result: QuickHoleResult;
    label: string;
    side: 'left' | 'neutral' | 'right';
    accent: string;
    soft: string;
  }> = [
    { result: 'team_a', label: teamAName, side: 'left', accent: accentA, soft: softA },
    {
      result: 'halved',
      label: 'Halved',
      side: 'neutral',
      accent: 'var(--bcast-ink-2)',
      soft: 'var(--bcast-card-2)',
    },
    { result: 'team_b', label: teamBName, side: 'right', accent: accentB, soft: softB },
  ];

  return (
    <section className="bcast-result-panel" aria-labelledby="match-result-control-title">
      <div className="bcast-result-copy">
        <span className="bcast-result-eyebrow">{eyebrow}</span>
        <h2 id="match-result-control-title" className="bcast-result-title">{title}</h2>
        <p className="bcast-result-helper">{helper}</p>
      </div>
      <div className="bcast-result-choices" role="group" aria-label={title}>
        {choices.map((choice) => (
          <button
            key={choice.result}
            type="button"
            className={`bcast-result-choice ${choice.side}${selected === choice.result ? ' is-selected' : ''}`}
            style={{
              '--result-accent': choice.accent,
              '--result-soft': choice.soft,
            } as React.CSSProperties}
            disabled={disabled || busy}
            aria-pressed={selected === choice.result}
            onClick={() => onSelect(choice.result)}
          >
            <span className="bcast-result-choice-label">{choice.label}</span>
            <span className="bcast-result-choice-state">
              {busy ? 'Saving' : selected === choice.result ? 'Selected' : 'Choose'}
            </span>
          </button>
        ))}
      </div>
      {disabled && readOnlyMessage
        ? <p className="bcast-result-readonly">{readOnlyMessage}</p>
        : null}
    </section>
  );
}

// ── FinalizeModal ─────────────────────────────────────────────────────────
function FinalizeModal({
  winnerName, marginLabel, accentColor, busy, error, onConfirm, onEdit,
}: {
  winnerName: string | null;
  marginLabel: string;
  accentColor: string;
  busy: boolean;
  error: string | null;
  onConfirm: () => void;
  onEdit: () => void;
}) {
  return (
    <div
      role="presentation"
      onClick={() => { if (!busy) onEdit(); }}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.7)',
        backdropFilter: 'blur(6px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 20,
        zIndex: 1200,
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Confirm match result"
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--bcast-card)',
          border: `1px solid ${accentColor}`,
          borderRadius: 16,
          padding: '28px 26px 22px',
          maxWidth: 460,
          width: '100%',
          maxHeight: '90vh',
          overflowY: 'auto',
          boxShadow: '0 30px 60px rgba(0,0,0,0.6)',
          color: 'var(--bcast-ink-1)',
          textAlign: 'center',
        }}
      >
        <div
          style={{
            fontSize: 11,
            fontWeight: 800,
            letterSpacing: '0.18em',
            color: 'var(--bcast-ink-3)',
            marginBottom: 8,
          }}
        >
          MATCH COMPLETE
        </div>
        <div
          style={{
            fontFamily: 'var(--bcast-condensed)',
            fontSize: 28,
            fontWeight: 800,
            color: 'var(--bcast-ink-1)',
            textTransform: 'uppercase',
            letterSpacing: '0.02em',
            lineHeight: 1.1,
          }}
        >
          {winnerName ? `${winnerName} WINS` : 'MATCH HALVED'}
        </div>
        {marginLabel && (
          <div
            className="bcast-tnum"
            style={{
              fontFamily: 'var(--bcast-condensed)',
              fontSize: 56,
              fontWeight: 800,
              color: accentColor,
              marginTop: 6,
              lineHeight: 1,
            }}
          >
            {marginLabel}
          </div>
        )}
        <p
          style={{
            color: 'var(--bcast-ink-2)',
            fontSize: 14,
            lineHeight: 1.55,
            margin: '18px 0 22px',
          }}
        >
          This locks in the result and updates the team score right away.
          A captain can correct the result later if something's off.
        </p>
        {error && (
          <div
            className="t-error"
            style={{ marginBottom: 14, textAlign: 'left' }}
          >
            {error}
          </div>
        )}
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'center' }}>
          <button
            type="button"
            onClick={onEdit}
            disabled={busy}
            style={{
              flex: '1 1 160px',
              padding: '14px 18px',
              borderRadius: 10,
              border: '1px solid var(--bcast-line)',
              background: 'var(--bcast-card-2)',
              color: 'var(--bcast-ink-1)',
              fontWeight: 700,
              fontSize: 14,
              cursor: busy ? 'not-allowed' : 'pointer',
            }}
          >
            Go back to edit
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            style={{
              flex: '1 1 160px',
              padding: '14px 18px',
              borderRadius: 10,
              border: 0,
              background: 'var(--bcast-accent)',
              color: 'var(--bcast-accent-ink)',
              fontWeight: 800,
              fontSize: 14,
              cursor: busy ? 'not-allowed' : 'pointer',
            }}
          >
            {busy ? 'Saving…' : 'Finalize match'}
          </button>
        </div>
      </div>
    </div>
  );
}

function MatchActionConfirmationModal({
  title,
  resultLabel,
  description,
  confirmLabel,
  busy,
  error,
  onCancel,
  onConfirm,
}: {
  title: string;
  resultLabel: string;
  description: string;
  confirmLabel: string;
  busy: boolean;
  error: string | null;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape' && !busy) onCancel();
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [busy, onCancel]);

  return (
    <div
      role="presentation"
      onClick={() => { if (!busy) onCancel(); }}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1300,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 'var(--screen-pad)',
        background: 'rgba(0,0,0,0.72)',
        backdropFilter: 'blur(6px)',
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="match-action-confirmation-title"
        onClick={(event) => event.stopPropagation()}
        style={{
          width: '100%',
          maxWidth: 440,
          padding: 'var(--s-xxxl)',
          border: '1px solid var(--bcast-line)',
          borderRadius: 'var(--card-radius)',
          background: 'var(--bcast-card)',
          color: 'var(--bcast-ink-1)',
          boxShadow: '0 24px 50px rgba(0,0,0,0.45)',
        }}
      >
        <h2 id="match-action-confirmation-title" style={{ margin: 0 }}>{title}</h2>
        <div
          style={{
            marginTop: 'var(--s-xxl)',
            color: 'var(--bcast-ink-1)',
            fontWeight: 800,
          }}
        >
          {resultLabel}
        </div>
        <p
          style={{
            margin: 'var(--s-lg) 0 var(--s-xxxl)',
            color: 'var(--bcast-ink-2)',
            lineHeight: 1.5,
          }}
        >
          {description}
        </p>
        {error ? <div className="t-error" style={{ marginBottom: 'var(--s-xxl)' }}>{error}</div> : null}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--s-xl)', flexWrap: 'wrap' }}>
          <button type="button" className="t-btn t-btn-ghost" onClick={onCancel} disabled={busy} autoFocus>
            Cancel
          </button>
          <button type="button" className="t-btn t-btn-primary" onClick={onConfirm} disabled={busy}>
            {busy ? 'Saving…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Helpers ───────────────────────────────────────────────────────────────

function namesFor(side: Participant[]): string {
  return side.map((p) => p.displayName.split(' ')[0] ?? p.displayName).join(' / ');
}

/** "Team Seagulls" → "SEAGULLS" so the banner's "TEAM " prefix doesn't
 *  double up into "TEAM TEAM SEAGULLS". */
function teamLabel(name: string | null | undefined, fallback: string): string {
  return ((name ?? '').replace(/^team\s+/i, '') || fallback).toUpperCase();
}

/** Derive status (which side is leading, what the label is, played +
 *  remaining hole counts) from the API's matchHoles + liveState fields. */
function deriveStatus(
  data: MatchScoringPayload | null,
  _palettes: { a: TeamPalette; b: TeamPalette },
): { side: 'a' | 'b' | 'tie'; label: string; played: number; remaining: number } {
  if (!data) return { side: 'tie', label: '', played: 0, remaining: TOTAL_HOLES };
  // Delegate to the shared lib/matchState helper. This is the same
  // function the BroadcastRoundSection (team view) uses for each row,
  // so the banner up here can't drift from the team-row label. See the
  // unit tests in lib/matchState.test.ts for the contract.
  const state = deriveMatchState({
    teamAId: data.match.teamAId,
    teamBId: data.match.teamBId,
    status: data.match.status,
    winningTeamId: (data.match as any).winningTeamId ?? null,
    finalResultType: (data.match as any).finalResultType ?? null,
    finalResultDisplay: data.liveState?.state.display ?? (data.match as any).finalResultDisplay ?? null,
    matchHoles: data.matchHoles,
  });
  return {
    side: state.leader,
    label: state.marginLabel,
    played: state.played,
    remaining: state.remaining,
  };
}

function HoleLegend({
  palettes,
  teamAName,
  teamBName,
}: {
  palettes: { a: TeamPalette; b: TeamPalette };
  teamAName: string;
  teamBName: string;
}) {
  const Item = ({ color, label }: { color: string; label: string }) => (
    <span className="bcast-hole-legend-item">
      <span className="bcast-hole-legend-dot" style={{ background: color }} />
      {label}
    </span>
  );
  return (
    <div className="bcast-hole-legend">
      <Item color={palettes.a.solid} label={teamAName} />
      <Item color={palettes.b.solid} label={teamBName} />
      <Item color="var(--bcast-ink-3)" label="Halved" />
    </div>
  );
}

// ── BroadcastPlayerRow ────────────────────────────────────────────────────
function BroadcastPlayerRow({
  player, palette, teamName, currentHole, par, value, disabled, onChange,
}: {
  player: Participant;
  palette: TeamPalette;
  teamName: string;
  currentHole: number;
  par: number | null;
  value: string;
  disabled: boolean;
  onChange: (next: string) => void;
}) {
  const strokes = (() => {
    const j = player.receivesStrokesJson;
    if (!j) return 0;
    const v = (j as any)[currentHole] ?? (j as any)[String(currentHole)];
    return Number(v) || 0;
  })();

  function bump(delta: number) {
    const n = parseInt(value, 10);
    const start = Number.isInteger(n) ? n : (par ?? 4);
    const next = Math.max(1, Math.min(20, start + delta));
    onChange(String(next));
  }

  return (
    <div
      className="bcast-player-row"
      style={{
        background: palette.soft,
        borderColor: palette.border,
      }}
    >
      {/* 38px (was 44) — gives the name column breathing room on
          narrow phones where the stepper cluster eats ~136px. */}
      <PlayerAvatar
        avatarUrl={player.avatarUrl}
        displayName={player.displayName}
        size={38}
      />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="bcast-player-name">{player.displayName}</div>
        <div className="bcast-player-meta">
          <span className="bcast-player-hcp">
            HCP {player.playingHandicap == null ? '—' : fmtHcp(player.playingHandicap)}
          </span>
          {strokes > 0 ? (
            <span className="bcast-stroke-chip">
              <StrokeDots count={strokes} />
              {strokes} stroke{strokes > 1 ? 's' : ''}
            </span>
          ) : null}
        </div>
        <div className="bcast-player-team">{teamName}</div>
      </div>
      <div className="bcast-stepper">
        <button
          type="button"
          className="bcast-stepper-btn"
          disabled={disabled}
          onClick={() => bump(-1)}
          aria-label="Decrease score"
        >
          −
        </button>
        <div className="bcast-stepper-num bcast-tnum" aria-live="polite">
          {value || '—'}
        </div>
        <button
          type="button"
          className="bcast-stepper-btn"
          disabled={disabled}
          onClick={() => bump(1)}
          aria-label="Increase score"
        >
          +
        </button>
      </div>
    </div>
  );
}

function StrokeDots({ count }: { count: number }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
      {Array.from({ length: count }, (_, i) => (
        <span
          key={i}
          style={{
            display: 'inline-block', width: 5, height: 5, borderRadius: '50%',
            background: 'var(--bcast-gold)',
          }}
        />
      ))}
    </span>
  );
}

function fmtHcp(n: number): string {
  const abs = Math.abs(n);
  const rounded = Math.round(abs * 10) / 10;
  const str = Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
  return n < 0 ? `+${str}` : str;
}

// ── OverrideResultModal ───────────────────────────────────────────────────
// Captain/owner shortcut to finalize a match without hole-by-hole entry.
// Three buttons: Team A wins / Team B wins / Tie. Optional reason for the
// audit trail. The page reloads after success so the match reflects final.
function OverrideResultModal({
  teamA, teamB, accentA, accentB, busy, error, onCancel, onConfirm,
}: {
  teamA: Team | null;
  teamB: Team | null;
  accentA: string;
  accentB: string;
  busy: boolean;
  error: string | null;
  onCancel: () => void;
  onConfirm: (resultType: 'team_a_win' | 'team_b_win' | 'tie', reason: string) => void;
}) {
  const [resultType, setResultType] = useState<'team_a_win' | 'team_b_win' | 'tie' | null>(null);
  const [reason, setReason] = useState('');

  useEffect(() => {
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape' && !busy) onCancel(); }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [busy, onCancel]);

  const choices: { key: 'team_a_win' | 'team_b_win' | 'tie'; label: string; color: string }[] = [
    { key: 'team_a_win', label: `${teamA?.name ?? 'Team A'} wins`, color: accentA },
    { key: 'team_b_win', label: `${teamB?.name ?? 'Team B'} wins`, color: accentB },
    { key: 'tie',        label: 'Tied (halved)',                    color: 'var(--bcast-ink-2)' },
  ];

  return (
    <div
      role="presentation"
      onClick={() => { if (!busy) onCancel(); }}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.66)',
        backdropFilter: 'blur(4px)',
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
        overflowY: 'auto', padding: '6vh 20px 20px', zIndex: 1000,
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="override-title"
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--card)', border: '1px solid var(--border)',
          borderRadius: 16, padding: 22, maxWidth: 440, width: '100%',
          boxShadow: '0 24px 50px rgba(0,0,0,0.45)',
          display: 'flex', flexDirection: 'column', gap: 14, flexShrink: 0,
        }}
      >
        <h2 id="override-title" style={{ margin: 0, fontSize: 20, fontWeight: 900, letterSpacing: '-0.01em', color: 'var(--text)' }}>
          Override match result
        </h2>
        <p style={{ margin: 0, color: 'var(--text-sec)', fontSize: 14, lineHeight: 1.5 }}>
          Skips hole entry and finalizes this match. Use when a match ended off-app or you need to correct a result. The points award immediately and team standings recompute.
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {choices.map((c) => {
            const active = resultType === c.key;
            return (
              <button
                key={c.key}
                type="button"
                onClick={() => setResultType(c.key)}
                disabled={busy}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '12px 14px', minHeight: 48,
                  border: `1px solid ${active ? c.color : 'var(--border)'}`,
                  background: active ? `${c.color}1A` : 'transparent',
                  color: 'var(--text)',
                  borderRadius: 10, fontWeight: 800, fontSize: 14, cursor: busy ? 'not-allowed' : 'pointer',
                  textAlign: 'left',
                }}
              >
                <span>{c.label}</span>
                <span style={{
                  width: 12, height: 12, borderRadius: 6,
                  background: active ? c.color : 'transparent',
                  border: `2px solid ${c.color}`,
                }} />
              </button>
            );
          })}
        </div>

        <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: 1.5, color: 'var(--text-sec)' }}>
            REASON (OPTIONAL)
          </span>
          <input
            type="text"
            className="t-input"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. Conceded on 14"
            maxLength={120}
            disabled={busy}
          />
        </label>

        {error && <div className="t-error" style={{ marginBottom: 0 }}>{error}</div>}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 4, flexWrap: 'wrap' }}>
          <button type="button" className="t-btn t-btn-ghost" onClick={onCancel} disabled={busy}>Cancel</button>
          <button
            type="button"
            className="t-btn t-btn-primary"
            onClick={() => resultType && onConfirm(resultType, reason)}
            disabled={busy || !resultType}
          >
            {busy ? 'Saving…' : 'Finalize match'}
          </button>
        </div>
      </div>
    </div>
  );
}
